import type { PrimaryMailboxSearchScope, SearchScope } from "owa-search-service";
import { getKey, SearchScopeKind, singleGroup3sSearchScope } from "owa-search-service";
import folderStore, { getFolderTable } from "owa-folders";
import { default as getSearchScopeDisplayName } from "./getSearchScopeDisplayName";
import shouldShowInScopeSelector from "./shouldShowInScopeSelector";
import shouldShowAllMailboxesScope from "./shouldShowAllMailboxesScope";
import { getGroupFolderId } from "owa-groups-shared-folders-store";
import { type GroupFolderWellKnownName } from "owa-folders-constants";
import { getGroupDisplayName } from "owa-group-utils/lib/utils/getGroupDisplayName";
import type { MailboxInfo } from "owa-client-types";
import { isMessageFolderRoot } from "owa-folders/lib/util/isFolderOrSubFolderOfDistinguishedFolder";
import { isFolderInFavorites } from "owa-favorites/lib/selectors/isInFavorites";
import { currentFolderScope, recursiveFolderScope, allMailboxesScope, favoritesFolderScope, } from "./getFolderContextualMenuOptions.locstring.json";
import loc from "owa-localize";
import { Menu, MenuItem, type MenuItemProps, MenuList, type MenuListProps, MenuPopover, MenuTrigger, makeStyles, mergeClasses, } from "@fluentui/react-components";
import React, { type FunctionComponent } from "react";
import { isUnifiedInboxMailboxInfo } from "owa-client-types/lib/UnifiedInboxMailboxInfo";

const useMenuItemStyles = makeStyles({
    root: {
        minWidth: "150px",
        "> span": {
            overflowX: "hidden",
            textOverflow: "ellipsis",
        },
    },
    selected: {
        fontWeight: "600",
    },
});

interface MailSearchScopeMenuListV9Props {
    searchScopes: SearchScope[];
    onSearchScopeSelected: (key: string) => void;
    selectedSearchScope: SearchScope;
    selectedMailboxInfo?: MailboxInfo;
}

/**
 * Returns array of dropdown options for folders contextual menu.
 */
export const MailSearchScopeMenuListV9: FunctionComponent<MailSearchScopeMenuListV9Props> = function MailSearchScopeMenuListV9(props: MailSearchScopeMenuListV9Props): React.ReactElement<MenuListProps> {
    const { searchScopes, onSearchScopeSelected, selectedSearchScope, selectedMailboxInfo } = props;
    const menuItemStyle = useMenuItemStyles();
    
    const menuItems: React.ReactElement<MenuItemProps>[] = [];
    const selectedScopeKey = getKey(selectedSearchScope);
    const selectedScopeAsMailboxSearchScope = selectedSearchScope as PrimaryMailboxSearchScope;
    const isGroupContext = selectedMailboxInfo?.type === "GroupMailbox";
    
    // Add the All folders folder first)
    // Add the UI for the Current folder,Subfolders, and Favorites folders (Without functionality for now)
    const favoritesFolderItems = getFavoritesMenuItems(onSearchScopeSelected, selectedScopeKey, menuItemStyle, selectedMailboxInfo);
    
    if (shouldShowAllMailboxesScope(selectedMailboxInfo)) {
        menuItems.push(<MenuItem key="allMailboxes" onClick={getOnScopeSelectedCallback(onSearchScopeSelected, SearchScopeKind.AllMailboxes)} className={mergeClasses(menuItemStyle.root, selectedSearchScope.kind === SearchScopeKind.AllMailboxes &&
                menuItemStyle.selected)} title={loc(allMailboxesScope)}>
                    {loc(allMailboxesScope)}
                </MenuItem>);
    }
    
    // Add the All folders
    menuItems.push(...getFolderMenuItems(searchScopes, onSearchScopeSelected, selectedScopeKey, menuItemStyle, selectedMailboxInfo));
    
    //Add current folder and subfolders only if not in a groups context
    if (!isGroupContext) {
        menuItems.push(<MenuItem key="currentFolder" onClick={getOnScopeSelectedCallback(onSearchScopeSelected, "currentFolder")} className={mergeClasses(menuItemStyle.root, selectedScopeAsMailboxSearchScope.isCurrentFolderScope &&
                menuItemStyle.selected)} title={loc(currentFolderScope)}>
                    {loc(currentFolderScope)}
                </MenuItem>);
        
        // Add the Subfolders option only if not in UIB
        if (selectedMailboxInfo && !isUnifiedInboxMailboxInfo(selectedMailboxInfo)) {
            menuItems.push(<MenuItem key="recursiveSearch" onClick={getOnScopeSelectedCallback(onSearchScopeSelected, "recursiveSearch")} className={mergeClasses(menuItemStyle.root, selectedScopeAsMailboxSearchScope.isSubfolderScope &&
                    menuItemStyle.selected)} title={loc(recursiveFolderScope)}>
                        {loc(recursiveFolderScope)}
                    </MenuItem>);
        }
        
        // Add favorites folder items if there are any
        if (favoritesFolderItems.length > 0) {
            menuItems.push(<Menu>
                        <MenuTrigger disableButtonEnhancement={true}>
                            <MenuItem key="favorites" title={loc(favoritesFolderScope)}>
                                {loc(favoritesFolderScope)}
                            </MenuItem>
                        </MenuTrigger>
                        <MenuPopover>
                            <MenuList> {favoritesFolderItems} </MenuList>
                        </MenuPopover>
                    </Menu>); // Separator
        }
    }
    else {
        // Add group-specific folder items when in group context
        const groupId = selectedMailboxInfo?.mailboxSmtpAddress;
        if (groupId && selectedMailboxInfo) {
            const inboxFolderId = getGroupFolderId(groupId, "inbox", selectedMailboxInfo);
            
            if (inboxFolderId) {
                const inboxScope = singleGroup3sSearchScope({
                    kind: SearchScopeKind.Group,
                    folderId: inboxFolderId,
                    groupId,
                });
                
                const inboxFolder = getFolderTable().get(inboxFolderId);
                const groupDisplayName = getGroupDisplayName(groupId);
                const inboxDisplayName = inboxFolder?.displayName || groupDisplayName || groupId;
                const inboxScopeKey = getKey(inboxScope);
                
                menuItems.push(<MenuItem key={inboxScopeKey} onClick={getOnScopeSelectedCallback(onSearchScopeSelected, inboxScopeKey)} className={mergeClasses(menuItemStyle.root, selectedScopeKey === inboxScopeKey && menuItemStyle.selected)} title={inboxDisplayName}>
                            {inboxDisplayName}
                        </MenuItem>);
            }
        }
    }
    return <MenuList>{menuItems}</MenuList>;
};
/**
 * Helper function to get the All folders and convert it
 * to an array of IContextualMenuItem objects to get rendered.
 */
function getFolderMenuItems(searchScopes: SearchScope[], onSearchScopeSelected: (key: string) => void, selectedScopeKey: string, menuItemStyle: {
    root: string;
    selected: string;
}, selectedMailboxInfo?: MailboxInfo): React.ReactElement<MenuItemProps>[] {
    const menuItems: React.ReactElement<MenuItemProps>[] = [];
    searchScopes
        ?.filter((scope: SearchScope) => {
        if (scope.kind !== SearchScopeKind.PrimaryMailbox &&
            scope.kind !== SearchScopeKind.ArchiveMailbox &&
            scope.kind !== SearchScopeKind.SharedFolders) {
            return false;
        }
        // Check if the folder exists and is a root folder
        const folder = folderStore().folderTable.get(scope.folderId);
        return folder && isMessageFolderRoot(folder);
    })
        .map((scope: SearchScope) => {
        if (shouldShowInScopeSelector(scope)) {
            const scopeKey = getKey(scope);
            const displayName = getSearchScopeDisplayName(scope, selectedMailboxInfo);
            menuItems.push(<MenuItem key={scopeKey} onClick={getOnScopeSelectedCallback(onSearchScopeSelected, scopeKey)} className={mergeClasses(menuItemStyle.root, selectedScopeKey === scopeKey && menuItemStyle.selected)} title={displayName}>
                        {displayName}
                    </MenuItem>);
        }
    });
    return menuItems;
}
/**
 * Helper function to get the user's Favorites folders and convert them
 * to an array of key-text pairs to get rendered as subMenuProps.
 */
function getFavoritesMenuItems(onSearchScopeSelected: (key: string) => void, selectedScopeKey: string, menuItemStyle: {
    root: string;
    selected: string;
}, selectedMailboxInfo?: MailboxInfo): React.ReactElement<MenuItemProps>[] {
    return Array.from(folderStore().folderTable.values())
        .filter(folder => {
        // Early exit for invalid folders or non-favorites
        if (!folder || !isFolderInFavorites(folder.id)) {
            return false;
        }
        
        // Check if mailbox type is supported
        const mailboxType = folder.mailboxInfo?.type;
        if (mailboxType !== "ArchiveMailbox" &&
            mailboxType !== "SharedMailbox" &&
            mailboxType !== "UserMailbox") {
            return false;
        }
        
        // Check if folder belongs to the selected mailbox
        return (selectedMailboxInfo?.mailboxSmtpAddress === folder.mailboxInfo.mailboxSmtpAddress);
    })
        .map(folder => {
        // Determine scope kind based on mailbox type
        let scopeKind: SearchScopeKind;
        const mailboxType = folder.mailboxInfo.type;
        
        if (mailboxType === "ArchiveMailbox") {
            scopeKind = SearchScopeKind.ArchiveMailbox;
        }
        else if (mailboxType === "SharedMailbox") {
            scopeKind = SearchScopeKind.SharedFolders;
        }
        else {
            scopeKind = SearchScopeKind.PrimaryMailbox;
        }
        
        const scope: SearchScope = {
            kind: scopeKind,
            folderId: folder.id,
        };
        const displayName = getSearchScopeDisplayName(scope, selectedMailboxInfo);
        const scopeKey = getKey(scope);
        
        return (<MenuItem key={scopeKey} onClick={getOnScopeSelectedCallback(onSearchScopeSelected, scopeKey)} className={mergeClasses(menuItemStyle.root, selectedScopeKey === scopeKey && menuItemStyle.selected)} title={displayName}>
                    {displayName}
                </MenuItem>);
    });
}

function getOnScopeSelectedCallback(onSearchScopeSelected: (key: string) => void, scopeKey: string): () => void {
    return () => {
        onSearchScopeSelected(scopeKey);
    };
}
